/**
 * AdminContactPage - mensagens do formulário de contacto público
 * Melhorias: marcar como tratado, pesquisa, exportação CSV, badge de estado
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/date";
import { Mail, CheckCircle2, Search, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminContactPage() {
    const [items, setItems] = useState([]);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all"); // "all" | "pending" | "handled"
    const [handling, setHandling] = useState(null);

    const load = () => api.get("/admin/contact-messages").then((r) => setItems(r.data));

    useEffect(() => { load(); }, []);

    const markHandled = async (id) => {
        setHandling(id);
        try {
            await api.patch(`/admin/contact-messages/${id}/handled`);
            setItems((prev) => prev.map((m) => m.id === id ? { ...m, handled: true } : m));
            toast.success("Mensagem marcada como tratada");
        } catch {
            toast.error("Erro ao atualizar mensagem");
        } finally {
            setHandling(null);
        }
    };

    const exportCsv = () => {
        const rows = [
            ["Nome", "Email", "Empresa", "Telefone", "Mensagem", "Data", "Tratada"],
            ...items.map((m) => [
                m.name || "",
                m.email || "",
                m.company || "",
                m.phone || "",
                (m.message || "").replace(/\n/g, " "),
                m.created_at ? new Date(m.created_at).toLocaleDateString("pt-PT") : "",
                m.handled ? "Sim" : "Não",
            ]),
        ];
        const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mesafa-contactos-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exportado");
    };

    const filtered = items.filter((m) => {
        const matchSearch = !search ||
            m.name?.toLowerCase().includes(search.toLowerCase()) ||
            m.email?.toLowerCase().includes(search.toLowerCase()) ||
            m.company?.toLowerCase().includes(search.toLowerCase()) ||
            m.message?.toLowerCase().includes(search.toLowerCase());
        const matchFilter =
            filter === "all" ||
            (filter === "pending" && !m.handled) ||
            (filter === "handled" && m.handled);
        return matchSearch && matchFilter;
    });

    const pendingCount = items.filter((m) => !m.handled).length;

    return (
        <div className="space-y-6" data-testid="admin-contact-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Contactos</h1>
                    <p className="text-muted-foreground flex items-center gap-2">
                        Mensagens recebidas pelo formulário de contacto.
                        {pendingCount > 0 && (
                            <Badge className="bg-[#FB7185]/20 text-[#FB7185] border-[#FB7185]/30">
                                {pendingCount} por tratar
                            </Badge>
                        )}
                    </p>
                </div>
                <Button variant="outline" onClick={exportCsv} className="gap-2" data-testid="admin-contact-export-csv">
                    <Download className="h-4 w-4" /> Exportar CSV
                </Button>
            </div>

            {/* Filtros */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Pesquisar nome, email, mensagem..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                    />
                </div>
                <div className="flex gap-2">
                    {[
                        { id: "all", label: "Todas" },
                        { id: "pending", label: "Por tratar" },
                        { id: "handled", label: "Tratadas" },
                    ].map((f) => (
                        <Button
                            key={f.id}
                            variant={filter === f.id ? "default" : "outline"}
                            size="sm"
                            onClick={() => setFilter(f.id)}
                            className={filter === f.id ? "btn-grad-purple text-white" : ""}
                        >
                            {f.label}
                        </Button>
                    ))}
                </div>
            </div>

            {filtered.length === 0 ? (
                <Card className="p-10 text-center bg-card border-dashed border-white/10">
                    <Mail className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground">
                        {search || filter !== "all" ? "Sem resultados para os filtros aplicados." : "Sem mensagens."}
                    </p>
                </Card>
            ) : (
                <div className="space-y-3">
                    {filtered.map((m) => (
                        <Card
                            key={m.id}
                            className={`glass-card p-4 rounded-2xl border-white/10 transition-opacity ${m.handled ? "opacity-60" : ""}`}
                            data-testid={`contact-${m.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="font-medium flex items-center gap-2">
                                        {m.name}
                                        {m.company && <span className="text-muted-foreground font-normal">· {m.company}</span>}
                                        {m.handled && (
                                            <Badge variant="outline" className="text-[10px] border-green-500/30 bg-green-500/10 text-green-400 gap-1">
                                                <CheckCircle2 className="h-2.5 w-2.5" /> Tratada
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {m.email} {m.phone ? `· ${m.phone}` : ""}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="text-xs text-muted-foreground">{fmtDateTime(m.created_at)}</span>
                                    {!m.handled && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => markHandled(m.id)}
                                            disabled={handling === m.id}
                                            className="gap-1 text-xs border-green-500/30 hover:bg-green-500/10 hover:text-green-400"
                                            data-testid={`contact-handle-${m.id}`}
                                        >
                                            {handling === m.id
                                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                                : <CheckCircle2 className="h-3 w-3" />}
                                            Marcar como tratada
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <p className="text-sm whitespace-pre-line">{m.message}</p>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
